import { useState } from 'react';
import {
  Box,
  Button,
  Code,
  Heading,
  Icon,
  InlineStack,
  Link,
  Panel,
  PanelActions,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  Search,
  Stack,
  Table,
  TableTbody,
  TableTh,
  TableThead,
  TableTr,
  Tooltip,
  TooltipContent,
  VisuallyHidden,
} from '@customerio/pluma-components/react';
import { CIO_OBJECT_ID_KEY, isObjectAttributeIdentifier } from '@/services/db/object/entity';
import { isEmptyString } from '@/utils/string';
import { AvailableAttributes } from './available-attributes';
import { ObjectAttributeRow } from './object-attribute-row';
import type { AttributeRow, ObjectAttributesEditorState } from './use-object-attributes';

const ENABLE_SEARCH_AFTER = 10;

type AttributesTableProps = {
  caption: string;
  rows: readonly AttributeRow[];
  editor: ObjectAttributesEditorState;
  isSaving: boolean;
  isMultiIdentifier: boolean;
};

function AttributesTable(props: AttributesTableProps) {
  return (
    <Table caption={props.caption} layout="fixed" isBorderless>
      <Box as="caption" pt="200" pb="050" px="300" textAlign="left">
        <Heading level="4">{props.caption}</Heading>
      </Box>
      <TableThead>
        <TableTr>
          <TableTh p="0" style={{ border: 'none', width: '300px' }}>
            <VisuallyHidden>Attribute name</VisuallyHidden>
          </TableTh>
          <TableTh p="0" style={{ border: 'none' }}>
            <VisuallyHidden>Attribute value</VisuallyHidden>
          </TableTh>
        </TableTr>
      </TableThead>
      <TableTbody>
        {props.rows.map((row) => (
          <ObjectAttributeRow
            key={row.id}
            row={row}
            errors={props.editor.errorsFor(row)}
            isNew={props.editor.isNew}
            isSaving={props.isSaving}
            showErrors={props.editor.isDirty}
            isMultiIdentifier={props.isMultiIdentifier}
            onKeyChange={props.editor.setKey}
            onValueChange={props.editor.setValue}
            onRemove={props.editor.remove}
          />
        ))}
      </TableTbody>
    </Table>
  );
}

type ObjectAttributesEditorProps = {
  editor: ObjectAttributesEditorState;
  sentAttributes: readonly string[];
  hasMoreAttributes: boolean;
  isMultiIdentifier: boolean;
  isSaving: boolean;
  canConfigureObjects: boolean;
  onSave: () => Promise<void>;
  onReset: () => void;
};

export function ObjectAttributesEditor(props: ObjectAttributesEditorProps) {
  const { editor } = props;
  const [searchTerm, setSearchTerm] = useState('');

  const mappableRows = editor.rows.filter((row) => !editor.isNew || row.key !== CIO_OBJECT_ID_KEY);
  const isSearchEnabled = !editor.isNew && mappableRows.length >= ENABLE_SEARCH_AFTER;
  const filteredRows = isEmptyString(searchTerm)
    ? mappableRows
    : mappableRows.filter((row) => row.key.includes(searchTerm));
  const identifierRows = filteredRows.filter((row) => isObjectAttributeIdentifier(row.key));
  const otherRows = filteredRows.filter((row) => !isObjectAttributeIdentifier(row.key));

  return (
    <>
      <InlineStack gap="200" alignBlock="stretch">
        <Panel
          as="section"
          withSectionBorders
          style={{ width: 'calc(8 / 12 * 100%)' }}
          data-test-attributes-edit-section
        >
          <PanelHeader>
            <PanelTitle>Attributes</PanelTitle>
            {isSearchEnabled ? (
              <PanelActions pl="150">
                <Search
                  data-test-attributes-search
                  ariaLabel="Attribute name"
                  size="sm"
                  placeholder="Find by attribute name"
                  value={searchTerm}
                  onSearchInput={setSearchTerm}
                  onSearch={setSearchTerm}
                  shouldSearchOnInput
                  w="full"
                  maxW="full"
                />
              </PanelActions>
            ) : null}
          </PanelHeader>

          {filteredRows.length === 0 ? (
            <PanelSection>
              <Box textAlign="center" p="300" data-test-attributes-not-found>
                <Paragraph variant="secondary">No attributes for this object matching "{searchTerm}"</Paragraph>
              </Box>
            </PanelSection>
          ) : (
            <>
              <PanelSection withPadding={false}>
                <AttributesTable
                  caption="Identifiers"
                  rows={identifierRows}
                  editor={editor}
                  isSaving={props.isSaving}
                  isMultiIdentifier={props.isMultiIdentifier}
                />
              </PanelSection>

              <PanelSection withPadding={false}>
                <AttributesTable
                  caption="Other attributes"
                  rows={otherRows}
                  editor={editor}
                  isSaving={props.isSaving}
                  isMultiIdentifier={props.isMultiIdentifier}
                />

                <Box mt="200" ml="250" mb="250">
                  <Tooltip isCustom isDisabled={props.canConfigureObjects}>
                    {(context) => (
                      <>
                        <Button
                          data-test-create-new-attribute
                          icon="add"
                          isDisabled={props.isSaving || searchTerm.length > 0 || !props.canConfigureObjects}
                          onClick={() => editor.create()}
                          ref={context.reference.setReference}
                          {...context.reference.getReferenceProps()}
                        >
                          Create new attribute
                        </Button>
                        <TooltipContent>You do not have permissions to configure objects.</TooltipContent>
                      </>
                    )}
                  </Tooltip>
                </Box>
              </PanelSection>
            </>
          )}
        </Panel>

        <Stack as="aside" gap="200" style={{ width: 'calc(4 / 12 * 100%)' }}>
          {editor.isNew ? (
            <Panel>
              <PanelHeader>
                <PanelTitle display="flex" alignItems="center" gap="100">
                  <Icon name="tip" size="lg" color="subtle" />
                  <span>About identifier attributes</span>
                </PanelTitle>
              </PanelHeader>
              <PanelSection>
                <Stack gap="200">
                  <Paragraph>
                    An identifier is a unique value used to identify objects when adding, updating, or tracking an
                    object.
                  </Paragraph>
                  <Paragraph>
                    <Code>object_id</Code> must be unique for each object and cannot be changed.{' '}
                    <Tooltip isCustom>
                      {(context) => (
                        <>
                          <Link
                            href="https://docs.customer.io/objects/#object-types"
                            variant="secondary"
                            isExternal
                            ref={context.reference.setReference}
                            {...context.reference.getReferenceProps()}
                          >
                            Learn more about object identifiers.
                          </Link>
                          <TooltipContent>Include object_id to add an object.</TooltipContent>
                        </>
                      )}
                    </Tooltip>
                  </Paragraph>
                </Stack>
              </PanelSection>
            </Panel>
          ) : (
            <Panel>
              <PanelHeader>
                <PanelTitle display="flex" alignItems="center" gap="100">
                  <Icon name="tip" size="lg" color="subtle" />
                  <span>How do I update attributes?</span>
                </PanelTitle>
              </PanelHeader>
              <PanelSection>
                <Paragraph>
                  You can do so by using this page, or by{' '}
                  <Link href="https://docs.customer.io/api/track/#operation/entity" variant="secondary" isExternal>
                    using the API
                  </Link>
                  .
                </Paragraph>
              </PanelSection>
            </Panel>
          )}

          <Panel>
            <PanelHeader>
              <PanelTitle>Available Attributes</PanelTitle>
            </PanelHeader>
            <PanelSection>
              <AvailableAttributes
                rows={mappableRows}
                sentAttributes={props.sentAttributes}
                hasMoreAttributes={props.hasMoreAttributes}
                isMultiIdentifier={props.isMultiIdentifier}
                isSaving={props.isSaving}
                onAddAttribute={editor.add}
              />
            </PanelSection>
          </Panel>
        </Stack>
      </InlineStack>

      {editor.isDirty ? (
        <InlineStack mt="300" alignInline="flex-end" alignBlock="center" gap="150">
          <Button variant="secondary" isCritical isDisabled={props.isSaving} onClick={props.onReset}>
            Discard changes
          </Button>
          <Tooltip isCustom isDisabled={!(editor.isInvalid || !props.canConfigureObjects) || props.isSaving}>
            {(context) => (
              <>
                <Button
                  variant="primary"
                  isDisabled={editor.isInvalid || !props.canConfigureObjects}
                  isLoading={props.isSaving}
                  onClick={() => void props.onSave()}
                  ref={context.reference.setReference}
                  {...context.reference.getReferenceProps()}
                >
                  Save changes
                </Button>
                <TooltipContent>
                  {props.canConfigureObjects
                    ? 'Fix errors before saving.'
                    : 'You do not have permissions to configure objects.'}
                </TooltipContent>
              </>
            )}
          </Tooltip>
        </InlineStack>
      ) : null}
    </>
  );
}
